/**
 * Fast local selection state for preferences picker.
 * Uses a ref-backed Set + version counter so list rows can read O(1) without passing new Set props each tap.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

function sortedIdsSignature(ids: number[]): string {
  return [...ids].sort((a, b) => a - b).join(',');
}

export function setsEqualSelection(a: Set<number>, b: number[]): boolean {
  if (a.size !== b.length) {
    return false;
  }
  for (const id of b) {
    if (!a.has(id)) {
      return false;
    }
  }
  return true;
}

function cloneSet(source: Set<number>): Set<number> {
  return new Set(source);
}

export function usePreferencesSelection(saved_ids: number[], max_selection: number) {
  const saved_signature = useMemo(() => sortedIdsSignature(saved_ids), [saved_ids]);
  const selection_ref = useRef<Set<number>>(new Set(saved_ids));
  const has_unsaved_ref = useRef(false);
  const [version, set_version] = useState(0);

  useEffect(() => {
    has_unsaved_ref.current = !setsEqualSelection(selection_ref.current, saved_ids);
  }, [version, saved_ids]);

  useEffect(() => {
    if (has_unsaved_ref.current) {
      return;
    }
    selection_ref.current = new Set(saved_ids);
    set_version(v => v + 1);
  }, [saved_signature, saved_ids]);

  const bump = useCallback(() => {
    set_version(v => v + 1);
  }, []);

  const is_selected = useCallback((id: number) => selection_ref.current.has(id), [version]);

  const selected_count = useMemo(() => selection_ref.current.size, [version]);

  const has_unsaved_changes = useMemo(
    () => !setsEqualSelection(selection_ref.current, saved_ids),
    [version, saved_ids],
  );

  const selected_ids_array = useMemo(() => Array.from(selection_ref.current), [version]);

  const toggle_id = useCallback(
    (id: number): 'toggled' | 'limit' | 'noop' => {
      const current = selection_ref.current;
      if (current.has(id)) {
        selection_ref.current = cloneSet(current);
        selection_ref.current.delete(id);
        bump();
        return 'toggled';
      }
      if (current.size >= max_selection) {
        return 'limit';
      }
      selection_ref.current = cloneSet(current);
      selection_ref.current.add(id);
      bump();
      return 'toggled';
    },
    [bump, max_selection],
  );

  const set_ids = useCallback(
    (ids: number[], mode: 'replace' | 'add' | 'remove') => {
      const current = selection_ref.current;
      const next = cloneSet(current);
      let hit_limit = false;

      if (mode === 'replace') {
        next.clear();
        for (const id of ids) {
          if (next.size >= max_selection) {
            hit_limit = true;
            break;
          }
          next.add(id);
        }
      } else if (mode === 'add') {
        for (const id of ids) {
          if (next.has(id)) {
            continue;
          }
          if (next.size >= max_selection) {
            hit_limit = true;
            break;
          }
          next.add(id);
        }
      } else {
        ids.forEach(id => next.delete(id));
      }

      let changed = next.size !== current.size;
      if (!changed) {
        for (const id of next) {
          if (!current.has(id)) {
            changed = true;
            break;
          }
        }
      }

      if (!changed) {
        return hit_limit ? ('limit' as const) : ('noop' as const);
      }

      selection_ref.current = next;
      bump();
      return hit_limit ? ('limit' as const) : ('toggled' as const);
    },
    [bump, max_selection],
  );

  const discard = useCallback(() => {
    selection_ref.current = new Set(saved_ids);
    bump();
  }, [saved_ids, bump]);

  const get_ids_for_save = useCallback(() => Array.from(selection_ref.current), [version]);

  return {
    version,
    is_selected,
    selected_count,
    selected_ids_array,
    has_unsaved_changes,
    toggle_id,
    set_ids,
    discard,
    get_ids_for_save,
  };
}
